import { Input as AntdInput } from 'antd';
import classNames from 'classnames';
import React, { ChangeEvent, FC, KeyboardEvent, ReactNode, memo, useEffect, useRef, useState } from 'react';

interface NumericInputProps {
  value: string | undefined;
  onCommit: (value: number) => void;
  prefix?: ReactNode;
  min?: number;
  max?: number;
  disabled?: boolean;
  className?: string;
  size?: 'small' | 'middle' | 'large';
  bordered?: boolean;
}

const clamp = (value: number, min: number | undefined, max: number | undefined): number => {
  let next = value;
  if (typeof min === 'number' && next < min) next = min;
  if (typeof max === 'number' && next > max) next = max;
  return next;
};

export const NumericInput: FC<NumericInputProps> = memo((props) => {
  const { value, onCommit, prefix, min, max, disabled, className, size = 'small', bordered = true } = props;

  const [draft, setDraft] = useState<string>(value ?? '');
  const lastCommittedRef = useRef<string>(value ?? '');
  const isFocusedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!isFocusedRef.current) {
      setDraft(value ?? '');
      lastCommittedRef.current = value ?? '';
    }
  }, [value]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setDraft(event.target.value);
  };

  const commit = (): void => {
    const trimmed = draft.trim();
    if (trimmed === '') {
      setDraft(lastCommittedRef.current);
      return;
    }

    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) {
      setDraft(lastCommittedRef.current);
      return;
    }

    const clamped = clamp(parsed, min, max);
    const clampedAsString = String(clamped);

    setDraft(clampedAsString);

    if (clampedAsString === lastCommittedRef.current) {
      return;
    }

    lastCommittedRef.current = clampedAsString;
    onCommit(clamped);
  };

  const handleBlur = (): void => {
    isFocusedRef.current = false;
    commit();
  };

  const handleFocus = (): void => {
    isFocusedRef.current = true;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      (event.target as HTMLInputElement).blur();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setDraft(lastCommittedRef.current);
      (event.target as HTMLInputElement).blur();
    }
  };

  return (
    <AntdInput
      className={classNames(className)}
      size={size}
      variant={bordered ? 'outlined' : 'borderless'}
      prefix={prefix}
      value={draft}
      disabled={disabled}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      inputMode="decimal"
    />
  );
});
